import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Mic, Square, Play, Pause, Trash2, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const audioTypes = [
  // Indigenous & Cultural
  { value: 'indigenous_elders', label: 'Indigenous Elders & Knowledge Holders' },
  { value: 'cultural_custodians', label: 'Cultural Custodians' },
  // Scientific & Research
  { value: 'ecologists', label: 'Ecologists' },
  { value: 'botanists', label: 'Botanists' },
  { value: 'mycorrhizal', label: 'Mycorrhizal Researchers' },
  { value: 'climate_scientists', label: 'Climate Scientists' },
  { value: 'fire_ecologists', label: 'Fire Ecologists' },
  { value: 'hydrologists', label: 'Hydrologists' },
  // Practitioners & Land Workers
  { value: 'arborists', label: 'Arborists' },
  { value: 'rangers', label: 'Rangers' },
  { value: 'land_managers', label: 'Land Managers' },
  { value: 'timber_workers', label: 'Timber Workers' },
  { value: 'agroforesters', label: 'Agroforesters' },
  // Community & Humanities
  { value: 'historians', label: 'Historians' },
  { value: 'local_community', label: 'Local Community' },
  { value: 'children', label: 'Children' },
  { value: 'artists', label: 'Artists' },
  { value: 'faith_leaders', label: 'Faith Leaders' },
  { value: 'philosophers', label: 'Philosophers' },
  { value: 'poets', label: 'Poets' },
  // Policy & Advocacy
  { value: 'planners', label: 'Planners' },
  { value: 'lawyers', label: 'Lawyers' },
  { value: 'rights_advocates', label: 'Rights-of-Nature Advocates' },
];

const AudioRecorderModal = ({ tree, onClose, onSuccess }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioType, setAudioType] = useState('indigenous_elders');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioURL) URL.revokeObjectURL(audioURL);
    };
  }, [audioURL]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) clearInterval(timerRef.current);
      }
      setIsPaused(!isPaused);
    }
  };

  const deleteRecording = () => {
    if (audioURL) URL.revokeObjectURL(audioURL);
    setAudioBlob(null);
    setAudioURL(null);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const togglePlayback = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUpload = async () => {
    if (!audioBlob) {
      toast.error('Please record audio first');
      return;
    }

    setLoading(true);
    try {
      // Step 1: Upload audio file to S3
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      
      // Convert webm to a file with proper name
      const audioFile = new File([audioBlob], `${audioType}-${Date.now()}.webm`, {
        type: 'audio/webm',
      });
      formData.append('file', audioFile);

      toast.info('Uploading audio file...', { duration: 2000 });
      
      const uploadResponse = await axios.post(`${API}/upload/audio`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const audioURL = uploadResponse.data.url;
      const audioS3Key = uploadResponse.data.s3_key;

      // Step 2: Save audio track metadata to tree
      await axios.post(
        `${API}/trees/${tree.id}/audio`,
        {
          type: audioType,
          url: audioURL,
          s3_key: audioS3Key,
          title: title || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Audio track uploaded and saved successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error uploading audio track:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload audio track');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="audio-recorder-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="bg-white border-b border-border px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3">
            <Mic className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-bold text-primary">Record Audio</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6" data-testid="audio-recorder-form">
          <div>
            <p className="text-sm text-muted-foreground mb-2">Recording for:</p>
            <p className="font-medium text-primary">{tree.name}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Audio Type *
            </label>
            <select
              value={audioType}
              onChange={(e) => setAudioType(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isRecording}
              data-testid="audio-type-select"
            >
              {audioTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Elder's Story"
              disabled={isRecording}
              data-testid="audio-title-input"
            />
          </div>

          {/* Recording Interface */}
          <div className="bg-secondary/30 rounded-lg p-6 space-y-4">
            <div className="text-center">
              <div className="text-4xl font-mono font-bold text-primary mb-2" data-testid="recording-timer">
                {formatTime(recordingTime)}
              </div>
              <div className="text-sm text-muted-foreground">
                {isRecording ? (isPaused ? 'Paused' : 'Recording...') : audioBlob ? 'Ready to upload' : 'Ready to record'}
              </div>
            </div>

            <div className="flex justify-center gap-3">
              {!isRecording && !audioBlob && (
                <button
                  onClick={startRecording}
                  className="flex items-center gap-2 px-6 py-3 bg-destructive text-white rounded-full font-medium hover:bg-destructive/90 transition-all shadow-md active:scale-95"
                  data-testid="start-recording-button"
                >
                  <Mic className="w-5 h-5" />
                  Start Recording
                </button>
              )}

              {isRecording && (
                <>
                  <button
                    onClick={pauseRecording}
                    className="p-4 bg-accent text-foreground rounded-full hover:bg-accent/90 transition-all shadow-md active:scale-95"
                    data-testid="pause-recording-button"
                  >
                    {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={stopRecording}
                    className="p-4 bg-destructive text-white rounded-full hover:bg-destructive/90 transition-all shadow-md active:scale-95"
                    data-testid="stop-recording-button"
                  >
                    <Square className="w-5 h-5" />
                  </button>
                </>
              )}

              {audioBlob && !isRecording && (
                <>
                  <button
                    onClick={togglePlayback}
                    className="p-4 bg-primary text-white rounded-full hover:bg-primary/90 transition-all shadow-md active:scale-95"
                    data-testid="play-recording-button"
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={deleteRecording}
                    className="p-4 bg-secondary border border-border text-foreground rounded-full hover:bg-secondary/80 transition-all shadow-md active:scale-95"
                    data-testid="delete-recording-button"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={startRecording}
                    className="p-4 bg-destructive text-white rounded-full hover:bg-destructive/90 transition-all shadow-md active:scale-95"
                    data-testid="re-record-button"
                  >
                    <Mic className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {audioURL && (
              <audio
                ref={audioPlayerRef}
                src={audioURL}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
            )}
          </div>

          <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> Audio recording is captured in your browser. In production, recordings would be uploaded to cloud storage (AWS S3, Cloudinary, etc.) and the URL would be saved to the database.
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-border rounded-lg font-medium hover:bg-secondary transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!audioBlob || loading}
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="upload-button"
            >
              <Upload className="w-4 h-4" />
              {loading ? 'Saving...' : 'Save Recording'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioRecorderModal;